import React, { useState, useEffect } from 'react';
import { Phone, ChevronDown, Menu, X } from 'lucide-react';
import { companyInfo, navLinks } from '../data/mock';
import Logo from './Logo';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}>
      {/* Top Bar - Hidden on mobile */}
      <div className="bg-[#0a2342] hidden sm:block">
        <div className="max-w-7xl mx-auto px-4 py-2">
          <div className="flex items-center justify-center sm:justify-start">
            <a href="tel:+16479513653" className="flex items-center gap-2 text-white/90 hover:text-[#D4AF37] transition-colors text-sm">
              <Phone size={14} />
              <span>Support: {companyInfo.phone.support}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <div className={`bg-white transition-all duration-300 ${isScrolled ? 'py-2 shadow-md' : 'py-3 sm:py-4'}`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <a href="#" className="flex items-center" aria-label="King of IT Group - Home">
              <Logo />
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-6 xl:gap-8" aria-label="Main navigation">
              {navLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  className="flex items-center gap-1 text-[#0a2342] font-medium hover:text-[#0077B6] transition-colors text-sm xl:text-base"
                >
                  {link.label}
                  {link.hasDropdown && <ChevronDown size={16} />}
                </a>
              ))}
            </nav>

            {/* CTA Buttons - Desktop */}
            <div className="hidden lg:flex items-center gap-3">
              <a
                href="#contact"
                className="px-5 xl:px-6 py-2.5 bg-[#0077B6] text-white font-medium rounded-full hover:bg-[#005f8f] transition-colors text-sm"
              >
                Book a Meeting
              </a>
              <a
                href="#contact"
                className="px-5 xl:px-6 py-2.5 border-2 border-[#0a2342] text-[#0a2342] font-medium rounded-full hover:bg-[#0a2342] hover:text-white transition-colors text-sm"
              >
                Get Pricing
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-[#0a2342] hover:bg-gray-100 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div 
          className={`lg:hidden fixed inset-0 top-[60px] sm:top-[100px] bg-white z-50 transform transition-transform duration-300 ease-in-out ${
            mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="h-full overflow-y-auto px-4 py-6">
            {/* Mobile Phone Number */}
            <a 
              href="tel:+16479513653" 
              className="flex items-center gap-2 text-[#0077B6] font-medium mb-6 sm:hidden"
            >
              <Phone size={18} />
              <span>{companyInfo.phone.support}</span>
            </a>
            
            <nav className="flex flex-col gap-1" aria-label="Mobile navigation">
              {navLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  className="text-[#0a2342] font-medium hover:text-[#0077B6] hover:bg-gray-50 transition-colors py-3 px-2 rounded-lg text-lg"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
            </nav>
            
            <div className="flex flex-col gap-3 pt-6 mt-6 border-t border-gray-100">
              <a
                href="#contact"
                className="px-6 py-3.5 bg-[#0077B6] text-white font-semibold rounded-full text-center hover:bg-[#005f8f] transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Book a Meeting
              </a>
              <a
                href="#contact"
                className="px-6 py-3.5 border-2 border-[#0a2342] text-[#0a2342] font-semibold rounded-full text-center hover:bg-[#0a2342] hover:text-white transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Get Pricing
              </a>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
